import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikProps, useFormik } from "formik";
import { ReactNode, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import { CustomSelect } from "../../../../../../../components/CustomSelect";
import CustomTimePicker from "../../../../../../../components/CustomTimePicker";
import Flex from "../../../../../../../components/Flex";
import Input from "../../../../../../../components/Input";
import MaskMultiSelect from "../../../../../../../components/MaskMultiSelect";
import Text from "../../../../../../../components/Text";
import ValueSwitcher from "../../../../../../../components/ValueSwitcher";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useGetPharmacyByType } from "../../../../../../../queries/patient-chart/order/use-get-pharmacy-by-type";
import { useAddLabRequisition } from "../../../../../../../queries/use-add-lab-requisition";
import { useEditLabRequisition } from "../../../../../../../queries/use-edit-lab-requisition";
// import { useLocationsList } from "../../../../../../../queries/use-locationList";
import AsyncSelect, {
  AsyncSelectValue,
} from "../../../../../../../components/AsyncSelect";
import ProviderSelect from "../../../../../../../components/ProviderSelect";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";
import { PatientOrderOrderTypeEnum } from "../../../../../../../services";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../../../../../../../utils/utils";
import PrintOrders from "../../../../patient/patient-list/patient-chart-new/order/orders/PrintOrders";

interface UpsertLabRequisitionFormProps {
  disclosureHost: DisclosureHost;
  isViewOnly?: boolean;
}

type LabForm = {
  uuid?: string;
  orderTitle: string;
  // location?: any;
  labs?: any;
  billType: "PATIENT" | "PROVIDER" | "INSURANCE";
  tests?: any;
  imagingCenter?: string;
  medicalCodes?: any;
  primaryProvider: any;
  orderingProvider: any;
  orderType: PatientOrderOrderTypeEnum;
  patientInstructions?: any;
  LabInstructionsTestCenter?: any;
  collectionDateTime?: Date;
  reminderTime?: string;
  labInstruction?: string;
  orderStatus?: string;
  orderFormType: "ELECTRONIC" | "PAPER";
};

const initialValues: LabForm = {
  orderTitle: "",
  // location: undefined,
  labs: undefined,
  billType: "PATIENT",
  tests: undefined,
  imagingCenter: undefined,
  primaryProvider: undefined,
  orderingProvider: undefined,
  orderType: "LAB",
  medicalCodes: undefined,
  patientInstructions: undefined,
  LabInstructionsTestCenter: undefined,
  collectionDateTime: new Date(),
  reminderTime: undefined,
  labInstruction: "",
  orderStatus: undefined,
  orderFormType: "PAPER",
};

const LAB_FORM_VALIDATION_SCHEMA = Yup.object({
  orderTitle: Yup.string().required().label("Order Title"),
  labs: Yup.array().when("orderType", {
    is: (type: string) => type !== "IMAGING",
    then: (schema) =>
      schema.min(1, "Lab is a required field").required().label("Lab"),
    otherwise: (schema) => schema.notRequired(),
  }),
  tests: Yup.array()
    .min(1, "Tests is a required field")
    .required()
    .label("Tests"),
  imagingCenter: Yup.string().when("orderType", {
    is: "IMAGING",
    then: (schema) =>
      schema.required("Imaging Center is required").label("Imaging Center"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const UpsertLabRequisitionForm = ({
  disclosureHost,
  isViewOnly,
}: UpsertLabRequisitionFormProps) => {
  const { patientId } = useParams();
  const [currentUser] = useCurrentUser();
  const patientUuid = patientId || currentUser?.uuid;

  const patientid: any = useSearchParams()[0].get("patientid");
  const appointmentid = useSearchParams()[0].get("appointmentid");
  const showSnackbar = useSnackbar();
  const [searchLab, setSearchLab] = useDebounce<string>("");
  const queryClient = useQueryClient();
  const { data: allLabs } = useGetPharmacyByType({
    pagination: DEF_PAGINATION,
    patientUuid: patientUuid,
    type: "LAB",
    searchString: searchLab,
    xTENANTID: getXtenantId(),
    enabled: disclosureHost.opened,
  });

  const allLabsOptions = useMemo(() => {
    return allLabs?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.name,
    }));
  }, [allLabs?.data?.content]);

  const { mutateAsync: addLabRequisition } = useAddLabRequisition();
  const { mutateAsync: editLabRequisition } = useEditLabRequisition();
  const realm = getXtenantId();
  const user = sessionStorage.getItem("user");
  const userData = user ? JSON.parse(user) : null;
  const hasToPrint = useRef<boolean>(false);

  const handleSubmit = (values: LabForm) => {
    const transFormData: any = getUnNecessaryTransformData(
      ["tests", "medicalCodes", "labs"],
      values
    );

    const payload = {
      patientOrder: {
        ...transFormData,
        primaryProvider: transFormData?.primaryProvider
          ? {
              [transFormData.primaryProvider?.value]:
                transFormData.primaryProvider?.label ?? "",
            }
          : undefined,
        orderingProvider: transFormData?.orderingProvider
          ? {
              [transFormData.orderingProvider?.value]:
                transFormData.orderingProvider?.label ?? "",
            }
          : undefined,
        patient: { [patientid ?? patientId]: patientUuid },
        appointmentId: appointmentid,
        provider: appointmentid
          ? {
              [userData.uuid]: `${userData?.firstName || ""} ${
                userData?.lastName || ""
              }`,
            }
          : undefined,
        orderStatus: values?.orderStatus,
      },
      xTENANTID: realm,
    };

    if (disclosureHost?.data)
      showToastAndResetForm(editLabRequisition(payload));
    else showToastAndResetForm(addLabRequisition(payload));
  };

  const closeAndReset = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  async function showToastAndResetForm(apiCall: Promise<any>) {
    await apiCall
      .then((res) => {
        if (!hasToPrint?.current) showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-orders-list"],
        });

        if (hasToPrint?.current)
          handleDownloadOrderReport(res?.data?.data || formik.values?.uuid);

        if (!hasToPrint?.current) closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  const formik = useFormik<LabForm>({
    initialValues: disclosureHost?.data || initialValues,
    validationSchema: LAB_FORM_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Editing an existing order: the fields that define what was ordered
  // (Order Type, Mode, Bill Type, Order Title, Lab/Imaging Center and Tests)
  // must not be changed after creation, so they are locked in edit mode.
  const isEditMode = Boolean((disclosureHost?.data as any)?.uuid);
  const isLocked = isEditMode || Boolean(isViewOnly);

  const [loinicCodeSearch, setLoinicCodeSearch] = useDebounce("");
  const { data: LOINCCODESRaw } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: loinicCodeSearch,
    type: "LOINC",
    active: undefined,
    archive: false,
    enabled: disclosureHost.opened,
  });

  const orderPrintViewDisclosure = useDisclosure(false, {
    onClose: () => {
      hasToPrint.current = false;
      closeAndReset();
    },
  });
  const handleDownloadOrderReport = async (patientOrderId?: string) => {
    if (!patientOrderId) return;
    orderPrintViewDisclosure.open(patientOrderId);
  };

  const [searchIcdKey, setSearchIcdKey] = useDebounce("");
  const { data: ICDCODESRaw } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: searchIcdKey,
    type: "ICD",
    active: undefined,
    archive: false,
    enabled: disclosureHost.opened,
  });

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Requisition Form"
      dialogAction={
        <Flex gap={2} height={"34px"} justifyContent={"end"}>
          {/* <StyleAddButton
            justifyContent={"center"}
            disableStartIcon
            w="205px"
            label="Save as Order Set"
            onClick={() => {}}
          /> */}
          <StyledButtonV2
            label="Save as Draft"
            variant="outlined"
            onClick={() => {
              formik.setFieldValue("orderStatus", ORDER_STATUS_OPTIONS.DRAFT);
              formik.handleSubmit();
            }}
            sx={{
              width: "139px",
              backgroundColor: "#CCECFF80",
              color: "#004186",
            }}
          />
          {/* <StyledButtonV2
            label="Print & Close"
            variant="outlined"
            onClick={() => {
              formik.setFieldValue("orderStatus", ORDER_STATUS_OPTIONS.ORDERED);
              hasToPrint.current = true;
              formik.submitForm();
            }}
            sx={{
              width: "139px",
              backgroundColor: "#CCECFF80",
              color: "#004186",
            }}
          /> */}
          <StyledButtonV2
            label="Order"
            variant="contained"
            disabled={isViewOnly}
            onClick={() => {
              formik.setFieldValue(
                "orderStatus",
                ORDER_STATUS_OPTIONS.RESULT_PENDING
              );
              formik.handleSubmit();
            }}
            sx={{ width: "90px" }}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "36vw",
          height: "70vh",
        },
      }}
    >
      <Flex pt={2}>
        {orderPrintViewDisclosure.data && (
          <PrintOrders disclosureHost={orderPrintViewDisclosure} />
        )}

        <Grid container spacing={2}>
          <Grid size={12}>
            <Flex justifyContent={"space-between"} gap={1}>
              <Flex gap={1} alignItems={"center"}>
                <Text mb={1}>Order Type</Text>
                <LockedControl locked={isLocked}>
                  <ValueSwitcher
                    alignment={formik.values?.orderType}
                    handleChange={(_, value) => {
                      if (isLocked) return;
                      formik.setFieldValue("orderType", value as any);
                    }}
                    timing={[
                      { value: "LAB", label: "Lab" },
                      { value: "IMAGING", label: "Imaging" },
                    ]}
                  />
                </LockedControl>
              </Flex>
              <Flex gap={1} alignItems={"center"}>
                <Text mb={1}>Mode</Text>
                <LockedControl locked={isLocked}>
                  <ValueSwitcher
                    alignment={formik.values?.orderFormType}
                    handleChange={(_, value) => {
                      if (isLocked) return;
                      formik.setFieldValue("orderFormType", value as any);
                    }}
                    timing={[
                      { value: "ELECTRONIC", label: "Electronic" },
                      { value: "PAPER", label: "Paper" },
                    ]}
                  />
                </LockedControl>
              </Flex>
            </Flex>
          </Grid>
          {/* <Grid item xs={6}>
              <MaskMultiSelect
                readOnly={isViewOnly}
                label="Locations"
                options={providerGroupLocationList?.data?.content?.map((l) => {
                  return {
                    uuid: l?.uuid,
                    name: l?.name,
                  };
                })}
                //   optionWithCheckbox
                value={formik.values?.location}
                onChange={(values) => formik.setFieldValue("location", values)}
              />
            </Grid> */}
          <Grid size={6}>
            <Input
              required
              label="Order Title"
              textFieldProps={{
                InputProps: { readOnly: isLocked },
                name: "orderTitle",
                value: formik.values.orderTitle,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                placeholder: "Order Title",
              }}
              error={formik.touched.orderTitle && formik.errors.orderTitle}
            />
          </Grid>
          <Grid size={6}>
            <Flex alignItems={"center"} justifyContent={"end"} gap={1}>
              <Text mr={1}>Bill Type</Text>
              <LockedControl locked={isLocked}>
                <ValueSwitcher
                  alignment={formik.values.billType}
                  handleChange={(_, value) => {
                    if (isLocked) return;
                    formik.setFieldValue("billType", value as any);
                  }}
                  timing={BILL_TYPE_LIST}
                />
              </LockedControl>
            </Flex>
          </Grid>
          {formik.values?.orderType === "IMAGING" && (
            <Grid size={12}>
              <CustomSelect
                readOnly={isLocked}
                label="Imaging Center For Order"
                value={formik.values?.imagingCenter}
                options={IMAGING_CENTER_OPTIONS}
                placeholder="Search Imaging Center For Order"
                onChange={(v) => formik.setFieldValue("imagingCenter", v)}
                required
                error={
                  formik.touched.imagingCenter &&
                  (formik.errors.imagingCenter as string)
                }
              />
            </Grid>
          )}

          {formik.values?.orderType === "LAB" && (
            <Grid container spacing={2} size={12}>
              <Grid size={12}>
                {/* ECRH-4764: Lab selection restricted to single select.
                <MaskMultiSelect
                  required
                  limitTags={1}
                  readOnly={isViewOnly}
                  label="Lab"
                  options={allLabsOptions}
                  placeholder="Search And Select"
                  onSearch={setSearchLab}
                  value={formik.values?.labs}
                  onChange={(values) => formik.setFieldValue("labs", values)}
                  error={formik.touched.labs && (formik.errors.labs as string)}
                /> */}
                <AsyncSelect
                  required
                  disabled={isLocked}
                  label="Lab"
                  options={allLabsOptions}
                  placeholder="Search And Select"
                  onSearch={setSearchLab}
                  name="labs"
                  handleBlur={formik.handleBlur}
                  // labs is kept as an array in form state so payload transform
                  // and validation stay unchanged; UI is single-select only.
                  value={formik.values?.labs?.[0] ?? null}
                  onChange={(val) =>
                    formik.setFieldValue("labs", val ? [val] : undefined)
                  }
                  error={formik.touched.labs && (formik.errors.labs as string)}
                />

                {/* <Input
                label="Lab"
                textFieldProps={{
                  name: "lab",
                  value: formik.values.lab,
                  onChange: formik.handleChange,
                  placeholder: "Enter Lab",
                }}
              /> */}
              </Grid>
            </Grid>
          )}

          <Grid size={12}>
            {/* <Input
              label="Tests"
              textFieldProps={{
                name: "",
                value: "",
                onChange: () => {},
                placeholder: "Follow Up...",
              }}
            /> */}

            <MaskMultiSelect
              label="Tests"
              limitTags={1}
              required
              error={formik.touched.tests && (formik.errors.tests as string)}
              readOnly={isLocked}
              options={LOINCCODESRaw?.data?.content?.map((l) => {
                return {
                  uuid: l?.uuid,
                  name: `${l?.code} - ${l?.description || ""}`,
                };
              })}
              onSearch={setLoinicCodeSearch}
              placeholder="Search And Select"
              value={formik.values?.tests}
              onChange={(values) => {
                formik.setFieldValue("tests", values);
              }}
            />
          </Grid>
          <Grid size={12}>
            {/* <Box>
            </Box> */}
            {/* <Input
              label="Tests"
              textFieldProps={{
                name: "",
                value: "",
                onChange: () => {},
                placeholder: "Follow Up...",
              }}
            /> */}

            <MaskMultiSelect
              label="Icd Code"
              readOnly={isViewOnly}
              options={ICDCODESRaw?.data?.content?.map((l) => {
                return {
                  uuid: l?.uuid,
                  name: `${l?.code} - ${l?.description}`,
                };
              })}
              onSearch={setSearchIcdKey}
              placeholder="Search And Select"
              value={formik.values?.medicalCodes}
              onChange={(values) => {
                formik.setFieldValue("medicalCodes", values);
              }}
            />
          </Grid>
          {/* <Grid item xs={12}>
            <StyleAddButton
              disableStartIcon
              w="140px"
              label="Save as Order Set"
              onClick={() => {}}
            />
          </Grid> */}

          <Grid size={12}>
            {/* <MaskMultiSelect
              label="Patient Instructions"
              options={[
                {
                  value: "tem",
                  label: "Temp",
                },
              ]}
              placeholder="Type"
              value={formik.values?.patientInstructions}
              onChange={(values) => {
                formik.setFieldValue("patientInstructions", values);
              }}
            /> */}

            <Input
              label="Patient Instructions"
              textFieldProps={{
                InputProps: { readOnly: isViewOnly },
                name: "patientInstruction",
                value: formik.values.patientInstructions,
                onChange: formik.handleChange,
                placeholder: "Patient Instructions",
              }}
            />
          </Grid>

          <Grid size={12}>
            {/* <MaskMultiSelect
              label="Lab Instructions To Test Center"
              options={[]}
              placeholder="Lab instructions to test center"
              value={formik.values?.LabInstructionsTestCenter}
              onChange={(values) => {
                formik.setFieldValue("LabInstructionsTestCenter", values);
              }}
            /> */}
            <Input
              label="Lab Instructions To Test Center"
              textFieldProps={{
                InputProps: { readOnly: isViewOnly },
                name: "labInstruction",
                value: formik.values.labInstruction,
                onChange: formik.handleChange,
                placeholder: "Lab instructions to test center",
              }}
            />
          </Grid>
          <Grid size={8}>
            <DateTimeControl formik={formik} />
          </Grid>

          {formik.values?.orderType === "IMAGING" && (
            <Grid size={6}>
              <ProviderSelect
                label="Primary Provider"
                name="primaryProvider"
                value={formik.values.primaryProvider as AsyncSelectValue}
                onChange={(val) => formik.setFieldValue("primaryProvider", val)}
                handleBlur={formik.handleBlur}
              />
            </Grid>
          )}

          {formik.values?.orderType === "IMAGING" && (
            <Grid size={6}>
              <ProviderSelect
                label="Ordering Provider"
                name="orderingProvider"
                value={formik.values.orderingProvider as AsyncSelectValue}
                onChange={(val) =>
                  formik.setFieldValue("orderingProvider", val)
                }
                handleBlur={formik.handleBlur}
              />
            </Grid>
          )}
          <Grid container size={12}>
            <RemindTimer formik={formik} />
          </Grid>
        </Grid>
      </Flex>
    </StyledFormModal>
  );
};

export default UpsertLabRequisitionForm;

// ValueSwitcher has no disabled state of its own, so a locked switcher is
// wrapped to block interaction and show it as inactive.
const LockedControl = ({
  locked,
  children,
}: {
  locked?: boolean;
  children: ReactNode;
}) => (
  <Box
    sx={{
      pointerEvents: locked ? "none" : "auto",
      opacity: locked ? 0.5 : 1,
    }}
  >
    {children}
  </Box>
);

const BILL_TYPE_LIST = [
  { value: "PATIENT", label: "Patient" },
  { value: "PROVIDER", label: "Provider" },
  { value: "INSURANCE", label: "Insurance" },
];

const DateTimeControl = ({ formik }: { formik: any }) => {
  const handleDateValue = (date: Date, type: "date" | "time") => {
    if (type === "time" && formik.values?.collectionDateTime) {
      const preDate = new Date(formik.values.collectionDateTime);
      preDate?.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
      formik.setFieldValue("collectionDateTime", preDate?.toISOString());
    } else formik.setFieldValue("collectionDateTime", date?.toISOString());
  };

  return (
    <Flex gap={2}>
      <CustomDatePicker
        label="Collection Date"
        name="collectionDateTime"
        value={dayjs(formik.values?.collectionDateTime)}
        onChange={(val) => handleDateValue(dayjs(val)?.toDate(), "date")}
      />
      <CustomTimePicker
        label="Collection Time"
        name="collectionDateTime"
        value={formik.values?.collectionDateTime}
        onChange={(val) => handleDateValue(dayjs(val)?.toDate(), "time")}
      />
    </Flex>
  );
};

const RemindTimer = ({ formik }: { formik: FormikProps<LabForm> }) => {
  const defaultReminders = ["12h", "24h", "3d", "7d", "2w", "1M"];
  const [allReminderTimes, _setAllReminderTimes] =
    useState<string[]>(defaultReminders);
  const handleChange = (v: string) => {
    formik.setFieldValue("reminderTime", v);
  };

  return (
    <Box>
      <Grid size={8}>
        <Text mb={1}> Reminder Time</Text>
        <Flex gap={1}>
          {allReminderTimes?.map((it) => (
            <CircularButton
              value={it}
              isActive={it === formik.values?.reminderTime}
              onClick={handleChange}
            />
          ))}
          {/* <CircularButton
            value={"+"}
            isActive={false}
            onClick={() => {
              setAllReminderTimes((prev) => [...prev, "1h"]);
            }}
          /> */}
        </Flex>
      </Grid>
      <Text mb={1} variant="caption" fontSize={"12px"} mt={1.5}>
        If result are not received within this time period, we'll send you a
        reminder to follow up with the patient
      </Text>
    </Box>
  );
};

const CircularButton = ({
  value,
  onClick,
  isActive,
}: {
  value: string;
  isActive: boolean;
  onClick?: (v: string) => void;
}) => {
  return (
    <Flex
      onClick={() => onClick?.(value)}
      minWidth={"40px"}
      sx={{ cursor: "pointer" }}
      borderRadius={"50%"}
      height={"40px"}
      bgcolor={"#1A1A1A26"}
      border={isActive ? "solid 1px black" : undefined}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Text>{value}</Text>
    </Flex>
  );
};

const IMAGING_CENTER_OPTIONS: { value: string; label: string }[] = [
  { value: "Imaging Center - 12345", label: "Imaging Center - 12345" },
  { value: "Imaging Center - 1234576", label: "Imaging Center - 1234576" },
  { value: "Imaging Center - 1873621", label: "Imaging Center - 1873621" },
  { value: "Other", label: "Other" },
];

export const ORDER_STATUS_OPTIONS: Record<string, string> = {
  DRAFT: "Draft",
  PRINTED: "Printed",
  ORDERED: "Ordered",
  RESULT_PENDING: "Pending",
  COMPLETED: "Completed",
  CANCELLED: "Canceled",
};
